import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { readNodeItemId } from './layer-cell-readers';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { readCreatureStatuses } from './creature-status-reader';
import { createVitalityReader } from './vitality-reader';
import { createStatusStackReader } from './status-stack-reader';
import type { StatusStackCatalog } from './status-stack-reader';
import { compileVitality } from './vitality-build';

export interface VitalityCompilerDependencies extends Required<ItemFieldDependencies> {
    catalogRows: ProviderCatalogRows;
    statusCatalog: StatusStackCatalog;
    vitalityCatalog: readonly Readonly<Record<string, unknown>>[];
    shellItemKeys: readonly string[];
    shellDropKeys: readonly string[];
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    /** Previously resolved shell fallback, only needed when supplied static catalog
     * data cannot answer. Absence must throw; a known null preserves PHP's ID fallback. */
    resolveShell?: (id: string) => ItemFieldRow | null;
    trace?: (event: readonly unknown[]) => void;
}
/** Request-owned composition of the whole vitality builder and its pure readers.
 * Supplied catalog metadata/results are inputs, never permission to fetch them.
 */
export function createVitalityCompiler(deps: VitalityCompilerDependencies): {
    compile: (target: ItemFieldRow, source?: ItemFieldRow | null) => { row: ItemFieldRow; burn: boolean };
    stack: (target: ItemFieldRow, baseId: string) => number | null;
    readers: {
        inventoryContext: InventoryReadContext;
        nodeItemId: (row: ItemFieldRow) => string | null;
        providers: (row: ItemFieldRow) => Iterable<ItemFieldRow>;
        scalar: (row: ItemFieldRow | null, key: string) => unknown;
        vitality: (row: ItemFieldRow | null) => ItemFieldRow | null;
    };
} {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return deps.resolveCarrierRef(id); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId, resolveCarrierRef: reference });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    const vitality = createVitalityReader({ inventoryContext, resolveInventoryInstance: deps.resolveInventoryInstance,
        legacyRead: path => emit(['legacy', path]) });
    const fields: ItemFieldDependencies = { resolveCarrierRef: reference,
        resolveItemName: id => { emit(['name', id]); return deps.resolveItemName(id); },
        resolveInventoryIdentity: id => { emit(['inventory-identity', id]); return deps.resolveInventoryIdentity(id); } };
    const field = (row: ItemFieldRow | null, key: string): unknown => readItemField(row, key, fields);
    const statuses = (row: ItemFieldRow): ItemFieldRow => readCreatureStatuses(row, 'target', reference);
    const stack = createStatusStackReader(deps.statusCatalog, { readStatuses: statuses, readItemField: field });
    const shell = (id: string): ItemFieldRow | null => {
        emit(['shell', id]);
        const entry = deps.vitalityCatalog.find(row => row.item_id === id);
        if (entry && entry.item_id != null && entry.item_name != null && entry.item_type != null) {
            const items: [string, unknown][] = [], layers: [string, unknown][] = [];
            for (const [key, value] of Object.entries(entry)) {
                if (deps.shellDropKeys.includes(key)) continue;
                (deps.shellItemKeys.includes(key) ? items : layers).push([key, value]);
            }
            return { items: Object.fromEntries(items), ...(layers.length ? { item_layer_types: Object.fromEntries(layers) } : {}) };
        }
        if (!deps.resolveShell) throw new Error(`Unavailable vitality status shell: ${id}`);
        return deps.resolveShell(id);
    };
    return { stack: stack.read, readers: { inventoryContext, nodeItemId, providers, scalar: scalar.read, vitality },
        compile: (target, source = null) => compileVitality(target, source, {
        readVitality: vitality, readLayerValue: scalar.read, readItemField: field, readStatuses: statuses,
        readBlightedStack: row => {
            const id = stack.id('blighted');
            if (id === null) throw new Error('Unavailable Blighted catalog ID');
            return stack.read(row, id);
        }, buildStatusShell: shell,
    }) };
}
